import React, {forwardRef, useImperativeHandle, useReducer} from 'react'

import moment from 'moment'
import {RRule} from 'rrule'

import time from 'server/utils/time.js'

import {Modal, ModalHeader, ModalActions, P, Alert, Button, S2} from '../UI/index.js'
import {DatePicker, FormStack, Recurrence} from '../UI/forms/index.js'

type CreateTemplatesPopupConfirmValues = {
    selectedOrderIds: string[]
    startDate: string
    endDate?: string
    rrule: string
}

type CreateTemplatesPopupProps = {
    onConfirm?: (values: CreateTemplatesPopupConfirmValues) => Promise<void> | void
}

export type CreateTemplatesPopupRef = {
    open: (ids: string[], defaultStartDate?: string) => void
    close: () => void
}

type CreateTemplatesPopupState = {
    isOpen: boolean
    selectedOrderIds: string[]
    startDate: string
    endDate?: string
    rrule: string
    loading: boolean
    error: string
}

const initialState: CreateTemplatesPopupState = {
    isOpen: false,
    selectedOrderIds: [],
    startDate: time.today(),
    endDate: undefined,
    rrule: '',
    loading: false,
    error: ''
}

const reducer = (state: CreateTemplatesPopupState, action: Partial<CreateTemplatesPopupState>): CreateTemplatesPopupState => {
    return {
        ...state,
        ...action
    }
}

const getDefaultRrule = (date: string): string => {
    return new RRule({
        freq: RRule.WEEKLY,
        interval: 1,
        byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
        dtstart: moment(date).hour(12).toDate()
    }).toString()
}

const CreateTemplatesPopup = forwardRef<CreateTemplatesPopupRef, CreateTemplatesPopupProps>(({onConfirm}, ref) => {
    const [{isOpen, selectedOrderIds, startDate, endDate, rrule, loading, error}, dispatch] = useReducer(reducer, initialState)

    const open = (ids: string[], defaultStartDate?: string) => {
        const selectedIds = Array.isArray(ids) ? ids : []
        const validStartDate = defaultStartDate && moment(defaultStartDate).isAfter(moment()) ? defaultStartDate : time.today()

        dispatch({
            isOpen: true,
            selectedOrderIds: [...selectedIds],
            startDate: validStartDate,
            endDate: undefined,
            rrule: getDefaultRrule(validStartDate),
            loading: false,
            error: ''
        })
    }

    const close = () => {
        dispatch(initialState)
    }

    const onClickConfirm = async () => {
        if (!selectedOrderIds.length) {
            dispatch({error: 'Selecteer minimaal 1 rit.'})
            return
        }

        if (!rrule) {
            dispatch({error: 'Selecteer een herhaling.'})
            return
        }

        if (endDate && moment(endDate).isBefore(moment(startDate))) {
            dispatch({error: 'Einddatum mag niet voor de startdatum liggen.'})
            return
        }

        dispatch({loading: true, error: ''})

        try {
            await onConfirm?.({
                selectedOrderIds: [...selectedOrderIds],
                startDate,
                endDate,
                rrule
            })

            close()
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : (err ? String(err) : 'Er is iets misgegaan bij het aanmaken van templates.')
            dispatch({loading: false, error: errorMessage})
        }
    }

    useImperativeHandle(ref, () => ({open, close}))

    return (
        <Modal show={isOpen} style={{maxWidth: 520}} onClose={close}>
            <ModalHeader title='Rit templates aanmaken' />

            <P style={{marginBottom: 16}}>Stel de herhaling en periode in voor alle geselecteerde ritten.</P>

            <FormStack>
                <DatePicker
                    label='Startdatum'
                    value={startDate}
                    minDate={time.today()}
                    maxDate={endDate}
                    onChange={(event) => {
                        const value = 'target' in event ? event.target.value : event.startDate
                        dispatch({startDate: value})
                    }}
                />

                <DatePicker
                    label='Einddatum'
                    value={endDate}
                    minDate={startDate > time.today() ? startDate : time.today()}
                    onChange={(event) => {
                        const value = 'target' in event ? event.target.value : event.endDate
                        dispatch({endDate: value || undefined})
                    }}
                    allowEmpty
                />

                <Recurrence
                    date={startDate}
                    rrule={rrule}
                    onChange={(event) => dispatch({rrule: event.target.value || ''})}
                    style={{marginBottom: 14 + 42}}
                />
            </FormStack>

            {error &&
                <Alert style={{marginBottom: 6}} variant='danger'>
                    {error}
                </Alert>
            }

            <ModalActions
                start={
                    <>
                        <i style={{fontSize: 12, marginRight: 3}} className='mdi mdi-check' />
                        <S2>{`${selectedOrderIds.length} ritten geselecteerd`}</S2>
                    </>
                }
            >
                <Button variant='text' onClick={close}>
                    Annuleren
                </Button>

                <Button variant='text' loading={loading} onClick={onClickConfirm}>
                    Bevestigen
                </Button>
            </ModalActions>
        </Modal>
    )
})

CreateTemplatesPopup.displayName = 'CreateTemplatesPopup'

export default CreateTemplatesPopup
